import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminRestaurants, getTowns, getFacets, setOutreachStatus } from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Download, ExternalLink, Search, Globe, MapPin, Loader2, Mail, Share2 } from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'restaurant', label: 'Restaurants' },
  { value: 'cafe', label: 'Cafés' },
  { value: 'takeaway', label: 'Takeaways' },
  { value: 'pub', label: 'Pubs & Bars' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'signed', label: 'Signed' },
  { value: 'not_interested', label: 'Not interested' },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
const STATUS_STYLE = {
  new: 'bg-[#EEEEEE] text-[#595959]',
  contacted: 'bg-[#E3F0FF] text-[#1E5AA8]',
  interested: 'bg-[#FFF4E0] text-[#A96A00]',
  signed: 'bg-[#E4F7E8] text-[#1E7A38]',
  not_interested: 'bg-[#FCE4E4] text-[#B23A3A]',
};
const PAGE_SIZE = 50;

const mapsLink = (placeId) => `https://www.google.com/maps/place/?q=place_id:${placeId}`;

const AdminRestaurantsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [towns, setTowns] = useState([]);
  const [town, setTown] = useState('all');
  const [category, setCategory] = useState('all');
  const [noWebsite, setNoWebsite] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => { getTowns().then((r) => setTowns(r.data)).catch(() => {}); }, []);

  const buildParams = useCallback((overrides = {}) => ({
    town_id: town === 'all' ? undefined : town,
    category: category === 'all' ? undefined : category,
    no_website: noWebsite || undefined,
    has_email: hasEmail || undefined,
    outreach: status === 'all' ? undefined : status,
    search: search.trim() || undefined,
    skip: page * PAGE_SIZE,
    limit: PAGE_SIZE,
    ...overrides,
  }), [town, category, noWebsite, hasEmail, status, search, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminRestaurants(buildParams());
      setRows(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setPage(0); }, [town, category, noWebsite, hasEmail, status, search]);

  const handleStatusChange = async (id, newStatus) => {
    const prev = rows;
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, outreach_status: newStatus } : r)));
    try {
      await setOutreachStatus(id, { status: newStatus });
      toast.success(`Marked ${STATUS_LABEL[newStatus]}`);
    } catch (e) {
      setRows(prev);
      toast.error('Failed to update status');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getAdminRestaurants(buildParams({ skip: 0, limit: 100000 }));
      const items = res.data.items;
      const headers = ['Name', 'Town', 'Postcode', 'Category', 'Cuisine', 'Hygiene', 'Phone', 'Email', 'Website', 'Facebook', 'Instagram', 'Google Listing', 'Status'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = [headers.join(',')];
      items.forEach((r) => {
        lines.push([
          r.name, r.town_name, r.postcode, r.venue_category, r.cuisine, r.hygiene_rating,
          r.phone, r.email, r.website_url, r.facebook, r.instagram,
          r.google_place_id ? mapsLink(r.google_place_id) : '',
          STATUS_LABEL[r.outreach_status] || 'New',
        ].map(esc).join(','));
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `restaurants-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} venues`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (authLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#7B2CBF]" />
      </div>
    );
  }

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-restaurants-page">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-[#7B2CBF] font-medium mb-4" data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#242E30]">Restaurant Directory</h1>
            <p className="text-[#595959] text-sm">{total} venue{total === 1 ? '' : 's'} matching filters</p>
          </div>
          <Button onClick={handleExport} disabled={exporting} className="bg-[#7B2CBF] hover:bg-[#5A189A] text-white" data-testid="export-csv-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E8E8E8] rounded-xl p-4 mb-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-[#595959] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name…" className="pl-9" data-testid="admin-rest-search" />
          </div>
          <Select value={town} onValueChange={setTown}>
            <SelectTrigger className="w-[180px]" data-testid="admin-rest-town"><SelectValue placeholder="All towns" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All towns</SelectItem>
              {towns.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[150px]" data-testid="admin-rest-category"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-[#242E30] cursor-pointer whitespace-nowrap">
            <Checkbox checked={noWebsite} onCheckedChange={(v) => setNoWebsite(!!v)} data-testid="admin-rest-no-website" />
            No website only
          </label>
          <label className="flex items-center gap-2 text-sm text-[#242E30] cursor-pointer whitespace-nowrap">
            <Checkbox checked={hasEmail} onCheckedChange={(v) => setHasEmail(!!v)} data-testid="admin-rest-has-email" />
            Has email only
          </label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px]" data-testid="admin-rest-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E8E8E8] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="restaurants-table">
              <thead className="bg-[#F5F5F5] text-[#595959] text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Town</th>
                  <th className="px-4 py-3 font-semibold">Postcode</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Cuisine</th>
                  <th className="px-4 py-3 font-semibold">Hygiene</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Website</th>
                  <th className="px-4 py-3 font-semibold">Social</th>
                  <th className="px-4 py-3 font-semibold">Google</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="px-4 py-10 text-center text-[#595959]"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={12} className="px-4 py-10 text-center text-[#595959]">No venues match these filters.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`restaurant-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium text-[#242E30]">{r.name}</td>
                    <td className="px-4 py-3 text-[#595959]">{r.town_name}</td>
                    <td className="px-4 py-3 text-[#595959]">{r.postcode}</td>
                    <td className="px-4 py-3 text-[#595959] capitalize">{r.venue_category}</td>
                    <td className="px-4 py-3 text-[#595959]">{r.cuisine}</td>
                    <td className="px-4 py-3 text-[#595959]">{r.hygiene_rating}</td>
                    <td className="px-4 py-3 text-[#595959] whitespace-nowrap">{r.phone}</td>
                    <td className="px-4 py-3">
                      {r.email ? (
                        <a href={`mailto:${r.email}`} className="text-[#7B2CBF] hover:underline inline-flex items-center gap-1" data-testid={`email-link-${r.id}`}>
                          <Mail className="h-3.5 w-3.5" /> {r.email}
                        </a>
                      ) : <span className="text-[#C0C0C0]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.website_url ? (
                        <a href={r.website_url} target="_blank" rel="noopener noreferrer" className="text-[#7B2CBF] hover:underline inline-flex items-center gap-1" data-testid={`website-link-${r.id}`}>
                          <Globe className="h-3.5 w-3.5" /> Visit
                        </a>
                      ) : <span className="text-[#C0392B] text-xs font-medium">None</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.facebook ? (
                        <a href={r.facebook} target="_blank" rel="noopener noreferrer" className="text-[#7B2CBF] hover:underline inline-flex items-center gap-1" data-testid={`social-link-${r.id}`}>
                          <Share2 className="h-3.5 w-3.5" /> FB
                        </a>
                      ) : <span className="text-[#C0C0C0]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.google_place_id ? (
                        <a href={mapsLink(r.google_place_id)} target="_blank" rel="noopener noreferrer" className="text-[#7B2CBF] hover:underline inline-flex items-center gap-1" data-testid={`google-link-${r.id}`}>
                          <MapPin className="h-3.5 w-3.5" /> Listing <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : <span className="text-[#C0C0C0]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Select value={r.outreach_status || 'new'} onValueChange={(v) => handleStatusChange(r.id, v)}>
                        <SelectTrigger className={`h-8 w-[140px] border-0 font-medium ${STATUS_STYLE[r.outreach_status || 'new']}`} data-testid={`status-select-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#F0F0F0]">
            <span className="text-sm text-[#595959]">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="prev-page">Previous</Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">Next</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRestaurantsPage;
